import { type ForwardRefExoticComponent, forwardRef } from 'react'

import { type GradientConfig, ICON_GRADIENTS } from '@ps/ds/icons/constants/gradients'
import { type IconNode, type LucideProps, createLucideIcon } from 'lucide-react'

import { getGradientUrl, renderGradientDef, resolveGradient } from './renderGradientDef'

export interface FilledIconProps extends LucideProps {
  /** Fill color or gradient (overrides gradient prop) */
  fill?: string
  /** Gradient for fill (overridden by fill prop) */
  gradient?: GradientConfig | keyof typeof ICON_GRADIENTS
}

interface FilledIconOptions {
  /** Default gradient */
  defaultGradient?: GradientConfig | keyof typeof ICON_GRADIENTS
}

/**
 * Creates a filled icon with support for solid colors and gradients.
 *
 * @example
 * ```tsx
 * const Icon = createFilledIcon('MyIcon', iconNode, {
 *   defaultGradient: 'blue-green',
 * })
 *
 * <Icon fill="currentColor" />
 * <Icon gradient="blue-green" />
 * ```
 */
export function createFilledIcon(
  iconName: string,
  iconNode: IconNode,
  options: FilledIconOptions = {},
): ForwardRefExoticComponent<FilledIconProps> {
  const { defaultGradient } = options

  // Create base icon component once outside render
  const BaseIconComponent = createLucideIcon(iconName, iconNode)

  const FilledIcon = forwardRef<SVGSVGElement, FilledIconProps>(
    ({ fill, gradient = defaultGradient, className, ...props }, ref) => {
      const gradientConfig = resolveGradient(gradient, ICON_GRADIENTS)

      // Determine fill value based on props
      let fillValue: string
      if (fill) {
        fillValue = fill
      } else if (gradientConfig) {
        fillValue = getGradientUrl(iconName, gradientConfig.id)
      } else {
        fillValue = 'currentColor'
      }

      return (
        <>
          {renderGradientDef(iconName, gradientConfig)}
          <BaseIconComponent
            ref={ref}
            fill={fillValue}
            className={className ? `lucide ${className}` : 'lucide'}
            {...props}
          />
        </>
      )
    },
  )

  FilledIcon.displayName = iconName

  return FilledIcon
}
